import { Injectable, OnDestroy } from '@angular/core';
import { ToastService } from './toast.service';
import { Subject, Subscription, filter, take } from 'rxjs';
import { SocketService } from './socket-service';


@Injectable({
  providedIn: 'root'
})
export class NotificationSocketService implements OnDestroy {
  private refresh$ = new Subject<any>();
  private notificationSubscription?: Subscription;
  private connectionSubscription?: Subscription;
  private isInitialized = false;
  private initAttempted = false;

  refreshNotifications$ = this.refresh$.asObservable();

  constructor(
    private socketService: SocketService,
    private toastService: ToastService
  ) { }

  /**
   * Initialize notification socket listener
   * Prevents multiple initializations and handles cleanup properly
   */
  init(): void {
    // Prevent multiple initialization attempts
    if (this.initAttempted) {
      // console.log('🔔 Notification service init already attempted');
      return;
    }

    this.initAttempted = true;

    // If already connected, initialize immediately
    if (this.socketService.isConnected()) {
      // console.log('🔔 Socket already connected, initializing notification listener immediately');
      this.initializeListener();
      return;
    }

    // Wait for socket connection
    // console.log('🔔 Waiting for socket connection...');
    
    // Clean up any existing connection subscription
    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
    }

    this.connectionSubscription = this.socketService.connection$
      .pipe(
        filter(connected => connected === true),
        take(1)
      )
      .subscribe({
        next: () => {
          // console.log('🔔 Socket connected, initializing notification listener');
          this.initializeListener();
          if (this.connectionSubscription) {
            this.connectionSubscription.unsubscribe();
            this.connectionSubscription = undefined;
          }
        },
        error: (error) => {
          // console.error('🔔 Error waiting for socket connection:', error);
          this.initAttempted = false; // Allow retry on error
          if (this.connectionSubscription) {
            this.connectionSubscription.unsubscribe();
            this.connectionSubscription = undefined;
          }
        }
      });
  }

  /**
   * Internal method to set up the notification listener
   */
  private initializeListener(): void {
    if (this.isInitialized) {
      // console.log('🔔 Notification listener already initialized, skipping');
      return;
    }

    if (!this.socketService.isConnected()) {
      // console.warn('🔔 Socket not connected, cannot initialize listener');
      this.initAttempted = false; // Allow retry
      return;
    }

    this.isInitialized = true;

    // Clean up any existing subscription
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }

    // console.log('🔔 Setting up notification listener...');
    
    // Subscribe to notification events
    this.notificationSubscription = this.socketService.listen<any>('notification')
      .subscribe({
        next: (notification: any) => {
          // console.log(' Notification received:', notification);
          // console.log(' Full notification data:', JSON.stringify(notification, null, 2));
          
          // Inform user immediately via toast
          this.toastService.showToast({
            type: 'info',
            summary: 'Form For Review',
            detail: notification.title || notification.message || 'You have a new notification'
          });

          // Emit notification data for UI components to update
          this.refresh$.next(notification);
        },
        error: (error: any) => {
          // console.error(' Notification socket error:', error);
          // Reset initialization flag on error to allow retry
          this.isInitialized = false;
          this.initAttempted = false;
        }
      });

    // console.log(' Notification listener initialized and active');
  }

  /**
   * Clean up all subscriptions and reset state
   */
  cleanup(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
      this.notificationSubscription = undefined;
    }

    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
      this.connectionSubscription = undefined;
    }
    
    this.isInitialized = false;
    this.initAttempted = false;
    // console.log(' Notification service cleaned up');
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
}